<template>
  <avtalat-modal
    :closeOnClickOutside="true"
    :closeOnEsc="true"
    :closeFn="onCloseClosedArendeModal"
    :idealHeight="1100"
  >
    <div class="flex h-full flex-col gap-sm px-md pt-md">
      <div class="flex flex-col gap-sm">
        <div class="flex">
          <button
            class="absolute right-md top-sm p-xs"
            data-testid="closed-arende-close-button"
            @click="onCloseClosedArendeModal"
          >
            <avtalat-icon class="text-black" id="kryss-enkelt" :size="15" />
          </button>
        </div>
        <div class="type-heading-md" data-testid="closed-arende-subject">
          {{ arende.rubrik }}
        </div>
        <ArendeIdDate
          :id="arende.id"
          :date="formatDateToYYYYMMDD(arende.createdAt)"
        />
        <FormGroup>
          <div>
            <div class="type-body-md">Kontaktorsak</div>
            <span class="font-bold" data-testid="closed-arende-kontaktorsak">{{
              arende.rubrik
            }}</span>
          </div>
          <div v-if="arende.fortydligadKontaktorsak">
            <div class="type-body-md">Förtydligad kontaktorsak</div>
            <span
              class="font-bold"
              data-testid="closed-arende-fortydligad-kontaktorsak"
              >{{ arende.fortydligadKontaktorsak }}</span
            >
          </div>
          <div data-testid="closed-arende-samtalsamnen">
            <div class="type-body-md mb-2xs">
              Samtalsämne ({{
                arende.samtalsamneIds !== null
                  ? arende.samtalsamneIds?.length
                  : 0
              }})
            </div>
            <ChipsContainer v-if="arende.samtalsamneIds !== null">
              <ChipsItem
                v-for="(samtalsamne, idx) in arende.samtalsamneIds"
                :removable="false"
                :key="idx"
              >
                {{ chipLabel(samtalsamne) }}
              </ChipsItem>
            </ChipsContainer>
          </div>
        </FormGroup>
        <FormGroup>
          <div class="type-body-md mb-2xs">
            Interna kommentarer ({{ arende.internaKommentarer?.length }})
          </div>
          <div class="custom-scrollbar max-h-[300px] overflow-y-auto">
            <InternaKommentarerList
              heading=""
              :interna-kommentarer="arende.internaKommentarer ?? []"
              :messageAlignement="false"
              :showVerified="false"
              :disable-expand="true"
              :can-edit="true"
            />
          </div>
        </FormGroup>
        <FormGroup class="type-body-md">
          <ClosedArendeInfo :arende="arende" />
        </FormGroup>
      </div>
      <div class="mt-auto flex justify-end space-x-sm pb-md">
        <avtalat-button
          outlined
          @click="onCloseClosedArendeModal"
          :loading="arendeStore.arende.isSaving"
          data-testid="closed-arende-ingen-atgard-button"
          >Ingen åtgärd</avtalat-button
        >
        <avtalat-button
          @click="onReopenArende"
          :loading="arendeStore.arende.isSaving"
          data-testid="closed-arende-ingen-ateroppna-button"
          >Återöppna ärende</avtalat-button
        >
      </div>
    </div>
  </avtalat-modal>
</template>

<script setup lang="ts">
import { inject } from "vue";

import { formatDateToYYYYMMDD } from "@/common/formatters";
import ArendeIdDate from "@/components/arende-components/ArendeIdDate.vue";
import InternaKommentarerList from "@/components/arende-components/InternaKommentarerList.vue";
import ChipsContainer from "@/components/ui/ChipsContainer.vue";
import ChipsItem from "@/components/ui/ChipsItem.vue";
import FormGroup from "@/components/ui/FormGroup.vue";
import type { Arbetsgivarearende } from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { useConfigStore } from "@/stores";

import { useArbetsgivarearendenStore } from "../../store";
import ClosedArendeInfo from "./ClosedArendeInfo.vue";

const configStore = useConfigStore();
const arendeStore = useArbetsgivarearendenStore();
const navigation = useNavigation();
const apiClient = useApi();

const openArendenAndReload = inject("openArendenAndReload", () => {});

const props = defineProps<{ arende: Arbetsgivarearende }>();

const onCloseClosedArendeModal = () => {
  navigation.closeArendeView();
};

const onReopenArende = async () => {
  await arendeStore.reopenArende(
    {
      arendeNumber: props.arende.id as string,
      isVerified: false,
      isManuallyVerified: false,
    },
    apiClient,
  );

  openArendenAndReload();
};

const chipLabel = (id: string) => configStore.getKontaktorsakById(id)?.namn;

export type SamtalsamneLabel = {
  id: string;
  label: string;
};
</script>
